import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, ROLE_PREVIEW_TARGETS } from '../contexts/AuthContext';
import { switchShell } from './InstallIdentity';
import {
  FolderKanban,
  Settings,
  LogOut,
  Plus,
  Menu,
  X,
  User,
  CalendarDays,
  FileText,
  Users,
  Camera,
  BookOpen,
  Wallet,
  Inbox,
  ShoppingBag,
  Compass,
  ChevronDown,
  Target,
  Eye,
  UserCog,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/button';
import { RolePreviewToggle, ROLE_LABELS } from './RolePreviewToggle';
import { NotificationsBell } from './NotificationsBell';
import { ImpersonateDialog } from './impersonation/ImpersonateDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_lux-workflow-test/artifacts/yi9ghwe1_Lux%20-%20Developments%20White%20and%20Orange%20With%20Grey%20Background.jpg";

const ORANGE_BTN = 'bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold';
const MENU_ITEM = 'text-gray-200 focus:text-white focus:bg-white/10 cursor-pointer';

export function Layout({ children }) {
  const {
    user, logout, isAdmin, canManageTenders, features,
    rolePreviewEnabled, isImpersonating, impersonateRole, setImpersonateRole, isViewingAsUser,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [impersonateOpen, setImpersonateOpen] = useState(false);

  // Main nav, in groups: Tenders · Projects · Schedule · Diary | Inbox · Ledger | Business
  const navGroups = [
    [
      ...(canManageTenders ? [{ path: '/tenders', label: 'Tenders', icon: FileText }] : []),
      { path: '/projects', label: 'Projects', icon: FolderKanban },
      { path: '/schedule', label: 'Schedule', icon: CalendarDays },
      // Site Diary — visible to admins & surveyors. Surveyors are locked
      // into the diary via DiaryRoute, so for them this is the only visible nav item.
      { path: '/diary', label: 'Diary', icon: BookOpen },
      ...((user?.capture_enabled || isAdmin) ? [{ path: '/captures', label: 'Captures', icon: Camera, desktopHidden: true }] : []),
    ],
    [
      ...(isAdmin ? [{ path: '/cost-inbox', label: 'Inbox', icon: Inbox }] : []),
      // Ledger also owns the Client POs tab (/client-pos) so it stays lit there.
      ...(isAdmin ? [{ path: '/cost-ledger', label: 'Ledger', icon: Wallet, also: ['/client-pos'] }] : []),
      ...(isAdmin && features?.purchase_orders ? [{ path: '/purchase-orders', label: 'Orders', icon: ShoppingBag }] : []),
    ],
    [
      ...(isAdmin ? [{ path: '/business', label: 'Business', icon: Compass }] : []),
    ],
  ].filter((g) => g.length);
  const contactsItem = { path: '/contacts', label: 'Contacts', icon: Users };
  const mainNav = navGroups.flat().filter((item) => !item.desktopHidden);

  const isPath = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const isActive = (item) => isPath(item.path) || (item.also || []).some(isPath);
  // The capture flow is a focused, full-screen task — hide the app's bottom nav
  // there so the 3 tabs don't confuse the user mid-capture.
  const hideBottomNav = location.pathname.startsWith('/capture');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const go = (to) => { setMenuOpen(false); navigate(to); };

  // One "+ New" menu — each item hands off to the create flow that page already owns.
  const createActions = [
    ...(canManageTenders ? [{ key: 'tender', label: 'New tender', icon: FileText, to: '/tenders/new' }] : []),
    { key: 'project', label: 'New project', icon: FolderKanban, to: '/projects/new' },
    { key: 'contact', label: 'New contact', icon: Users, to: '/contacts?new=1' },
    { key: 'diary', label: 'New diary entry', icon: BookOpen, to: '/diary?new=note' },
    ...(isAdmin ? [{ key: 'goal', label: 'New goal', icon: Target, to: '/business?view=plan&new=goal' }] : []),
  ];

  const showPreview = rolePreviewEnabled && (isAdmin || isImpersonating);
  // "Impersonate a user" is only ever offered to a genuine admin who isn't already viewing as someone.
  const canImpersonate = isAdmin && !isImpersonating && !isViewingAsUser;
  const previewOptions = [{ key: 'admin', label: 'Admin (you)' }, ...ROLE_PREVIEW_TARGETS.map((r) => ({ key: r, label: ROLE_LABELS[r] }))];
  const currentPreview = impersonateRole || 'admin';
  const pickPreview = (role) => setImpersonateRole(role === 'admin' ? null : role);

  // Format roles for display
  const formatRoles = (roles) => {
    if (!roles) return '';
    if (Array.isArray(roles)) {
      return roles.map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(', ');
    }
    return roles.charAt(0).toUpperCase() + roles.slice(1);
  };

  const navLinkClass = (active) => `flex items-center gap-2 px-2.5 min-[1360px]:px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
    active ? 'bg-[#F5A623]/10 text-[#F5A623]' : 'text-gray-400 hover:text-white hover:bg-white/5'
  }`;

  return (
    <div className="app-container">
      {/* Desktop Header */}
      <header className="desktop-header">
        <div className="flex items-center gap-5 min-w-0">
          {/* Hamburger — main nav collapses into the menu under 1200px */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="nav-hamburger"
            className="min-[1200px]:hidden text-gray-300 hover:text-white -ml-2 shrink-0"
            aria-label="Open navigation"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>

          <Link to="/projects" className="flex items-center shrink-0">
            <img
              src={LOGO_URL}
              alt="LUX Developments"
              className="h-10 w-auto object-contain"
            />
          </Link>

          <nav className="hidden min-[1200px]:flex items-center gap-0.5 min-[1360px]:gap-1 shrink-0" data-testid="main-nav">
            {navGroups.map((group, gi) => {
              const items = group.filter((item) => !item.desktopHidden);
              if (!items.length) return null;
              return (
                <div key={gi} className="flex items-center gap-0.5 min-[1360px]:gap-1">
                  {gi > 0 && <span className="w-px h-5 bg-white/10 mx-1 min-[1360px]:mx-1.5" aria-hidden data-testid={`nav-divider-${gi}`} />}
                  {items.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                      className={navLinkClass(isActive(item))}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              );
            })}
          </nav>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {/* Contacts — secondary link, muted until hovered/active */}
          <Link
            to="/contacts"
            data-testid="nav-contacts"
            className={`hidden min-[1200px]:flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
              isPath('/contacts') ? 'text-[#F5A623] bg-[#F5A623]/10 font-medium' : 'text-gray-500 hover:text-gray-200 hover:bg-white/5'
            }`}
          >
            <Users className="w-4 h-4" />
            Contacts
          </Link>

          {/* + New */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="new-menu-trigger" className={ORANGE_BTN}>
                <Plus className="w-4 h-4 mr-1.5" />
                New
                <ChevronDown className="w-4 h-4 ml-1.5 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 bg-[#2A3240] border-white/10" data-testid="new-menu">
              {createActions.map((a) => (
                <DropdownMenuItem key={a.key} onClick={() => navigate(a.to)} data-testid={`new-menu-${a.key}`} className={MENU_ITEM}>
                  <a.icon className="w-4 h-4 mr-2 text-[#F5A623]" />
                  {a.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {isAdmin && <NotificationsBell />}

          {/* User menu — profile, Settings, Admin (role preview), Logout */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                data-testid="user-menu-trigger"
                className="flex items-center gap-2 text-gray-400 hover:text-white px-2"
              >
                <div className="w-8 h-8 rounded-full bg-[#F5A623]/20 flex items-center justify-center">
                  <User className="w-4 h-4 text-[#F5A623]" />
                </div>
                <span className="hidden min-[1360px]:inline">{user?.name}</span>
                {isImpersonating && (
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#F5A623] text-[#1E2530]" data-testid="user-menu-preview-pill">
                    {ROLE_LABELS[impersonateRole]}
                  </span>
                )}
                <ChevronDown className="w-4 h-4 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60 bg-[#2A3240] border-white/10" data-testid="user-menu">
              <DropdownMenuLabel className="text-gray-400">
                <div className="text-gray-200">{user?.name}</div>
                <div className="text-xs font-normal">{user?.email}</div>
                <div className="text-xs font-normal mt-1 text-[#F5A623]">{formatRoles(user?.roles || user?.role)}</div>
              </DropdownMenuLabel>
              {isAdmin && (
                <>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="user-menu-settings" className={MENU_ITEM}>
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                </>
              )}
              {(showPreview || canImpersonate) && (
                <>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1.5" data-testid="user-menu-admin-heading">
                    <Eye className="w-3.5 h-3.5" /> {showPreview ? 'Admin · preview as' : 'Admin'}
                  </DropdownMenuLabel>
                  {showPreview && previewOptions.map((o) => (
                    <DropdownMenuItem key={o.key} onClick={() => pickPreview(o.key)} data-testid={`user-menu-preview-${o.key}`} className={`${MENU_ITEM} justify-between`}>
                      <span>{o.label}</span>
                      {currentPreview === o.key && <span className="text-[#F5A623]">●</span>}
                    </DropdownMenuItem>
                  ))}
                  {canImpersonate && (
                    <DropdownMenuItem onClick={() => setImpersonateOpen(true)} data-testid="user-menu-impersonate" className={`${MENU_ITEM} ${showPreview ? 'mt-1 border-t border-white/10 rounded-none pt-2' : ''}`}>
                      <UserCog className="w-4 h-4 mr-2 text-[#F5A623]" />
                      Impersonate a user…
                    </DropdownMenuItem>
                  )}
                </>
              )}
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem
                onClick={handleLogout}
                data-testid="logout-btn"
                className="text-red-400 focus:text-red-400 focus:bg-red-500/10 cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="md:hidden flex items-center justify-between p-4 bg-[#2A3240] border-b border-white/10" style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))' }}>
        <Link to="/projects" className="flex items-center">
          <img
            src={LOGO_URL}
            alt="LUX Developments"
            className="h-8 w-auto object-contain"
          />
        </Link>

        <div className="flex items-center gap-2 relative z-50">
          {isAdmin && <NotificationsBell />}
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => switchShell(navigate, '/diary')}
              data-testid="mobile-diary-link"
              title="Site Diary"
              className="text-gray-300 hover:text-white"
            >
              <BookOpen className="w-5 h-5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="mobile-menu-toggle"
            className="relative z-50"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      {/* Collapsed menu — phones and anything under 1200px */}
      {menuOpen && (
        <div className="min-[1200px]:hidden fixed inset-0 z-[60] bg-[#1E2530]/95 backdrop-blur-sm animate-fade-in overflow-y-auto" data-testid="nav-menu">
          <div className="flex flex-col min-h-full p-6 pb-10 max-w-md" style={{ paddingBottom: 'calc(2.5rem + env(safe-area-inset-bottom, 0px))' }}>
            <div className="flex justify-between items-center mb-8">
              <img
                src={LOGO_URL}
                alt="LUX Developments"
                className="h-8 w-auto object-contain"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMenuOpen(false)}
                data-testid="nav-menu-close"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <nav className="flex flex-col gap-1">
              {navGroups.map((group, gi) => (
                <div key={gi} className="flex flex-col gap-1">
                  {gi > 0 && <div className="border-t border-white/10 my-2" aria-hidden />}
                  {group.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMenuOpen(false)}
                      data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                      className={`flex items-center gap-3 px-4 py-3 rounded-md text-lg font-medium transition-colors ${
                        isActive(item) ? 'bg-[#F5A623]/10 text-[#F5A623]' : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              ))}
              <div className="border-t border-white/10 my-2" aria-hidden />
              <Link
                to={contactsItem.path}
                onClick={() => setMenuOpen(false)}
                data-testid="mobile-nav-contacts"
                className={`flex items-center gap-3 px-4 py-3 rounded-md text-lg transition-colors ${
                  isPath('/contacts') ? 'bg-[#F5A623]/10 text-[#F5A623] font-medium' : 'text-gray-500 hover:text-white hover:bg-white/5'
                }`}
              >
                <Users className="w-5 h-5" />
                Contacts
              </Link>
            </nav>

            {/* Create actions */}
            <div className="mt-6" data-testid="mobile-create-actions">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-2 px-1">New</p>
              <div className="grid grid-cols-2 gap-2">
                {createActions.map((a) => (
                  <Button
                    key={a.key}
                    onClick={() => go(a.to)}
                    data-testid={`mobile-new-${a.key}`}
                    className={`${ORANGE_BTN} justify-start h-11 text-sm`}
                  >
                    <Plus className="w-4 h-4 mr-2 shrink-0" /> <span className="truncate">{a.label}</span>
                  </Button>
                ))}
              </div>
            </div>

            {/* Admin tools (phones have no avatar menu) */}
            {(isAdmin || isImpersonating) && (
              <div className="mt-6 md:hidden" data-testid="mobile-admin-tools">
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-2 px-1">Admin</p>
                {isAdmin && (
                  <Link to="/settings" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-settings"
                    className={`flex items-center gap-3 px-4 py-3 rounded-md text-base transition-colors ${isPath('/settings') ? 'bg-[#F5A623]/10 text-[#F5A623]' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
                    <Settings className="w-5 h-5" /> Settings
                  </Link>
                )}
                {showPreview && (
                  <div className="mt-3 px-1" data-testid="mobile-role-preview">
                    <p className="text-xs text-gray-500 mb-2">Preview as</p>
                    <RolePreviewToggle theme="dark" />
                  </div>
                )}
                {canImpersonate && (
                  <button type="button" onClick={() => { setMenuOpen(false); setImpersonateOpen(true); }} data-testid="mobile-nav-impersonate"
                    className="mt-2 w-full flex items-center gap-3 px-4 py-3 rounded-md text-base text-gray-400 hover:text-white hover:bg-white/5 transition-colors">
                    <UserCog className="w-5 h-5 text-[#F5A623]" /> Impersonate a user…
                  </button>
                )}
              </div>
            )}

            <div className="mt-auto pt-6 border-t border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#F5A623]/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#F5A623]" />
                </div>
                <div>
                  <div className="font-medium">{user?.name}</div>
                  <div className="text-sm text-gray-400">{formatRoles(user?.roles || user?.role)}</div>
                </div>
              </div>
              <Button
                variant="ghost"
                onClick={handleLogout}
                data-testid="mobile-logout-btn"
                className="w-full justify-start text-red-400 hover:text-red-400 hover:bg-red-500/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="main-content">
        {children}
      </main>

      {canImpersonate && <ImpersonateDialog open={impersonateOpen} onOpenChange={setImpersonateOpen} />}

      {/* Mobile Bottom Navigation — hidden during the capture flow and while the menu is open */}
      {!hideBottomNav && !menuOpen && (
        <nav className="mobile-nav">
          {mainNav.slice(0, 3).map(item => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`bottom-nav-${item.label.toLowerCase()}`}
              className={`mobile-nav-item ${isActive(item) ? 'active' : ''}`}
            >
              <item.icon />
              {item.label}
            </Link>
          ))}
        </nav>
      )}
    </div>
  );
}
